import {
  Box,
  Button,
  MenuItem,
  Select,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ColumnSelectOption } from '@/shared/ui';
import { ColumnSelect, LabeledSwitcher, ToggleButtonGroup } from '@/shared/ui';
import type { AxisSeriesSettings } from '../../model/types';
import {
  BY_METRIC_LINE_WIDTH,
  FIXED_LINE_WIDTHS,
  SeriesChartType,
  SeriesLineStyle,
  SeriesPercentDiff,
  SeriesValueLabels,
  VALUE_LABEL_FONT_SIZES,
} from '../../model/types';
import {
  MAX_METRIC_LINE_WIDTH,
  MIN_METRIC_LINE_WIDTH,
} from '../SeriesChart/constants';
import {
  OptionLabel,
  byMetricPreview,
  getChartTypePreview,
  getLineStylePreview,
  getLineWidthPreview,
  percentDiffPreview,
  valueLabelsPreview,
} from './previews';

const BoldToggle = styled(ToggleButton)(({ theme }) => ({
  height: 40,
  paddingInline: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  textTransform: 'none',
  fontWeight: 700,

  '&.Mui-selected': {
    color: '#063FFE',
    backgroundColor: '#EBEFFF',
  },
}));

const BAR_DISABLED_REASON =
  'Bars have no stroke — pick Line, Step or Area to change this.';

const LABELS_OFF_DISABLED_REASON =
  'Needs value labels — with none shown there is nothing to overlap.';

export const BY_METRIC_SPLIT_DISABLED_REASON =
  'Needs a Split: with a single series there is nothing to compare widths against.';

interface Props {
  title: string;
  settings: AxisSeriesSettings;
  defaults: AxisSeriesSettings;
  widthMetricOptions: ColumnSelectOption[];
  byMetricDisabledReason?: string;
  onChange: (patch: Partial<AxisSeriesSettings>) => void;
}

const isPristine = (
  settings: AxisSeriesSettings,
  defaults: AxisSeriesSettings,
): boolean =>
  (Object.keys(defaults) as (keyof AxisSeriesSettings)[]).every(
    (key) => settings[key] === defaults[key],
  );

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" sx={{ color: '#7E828F' }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export function AxisSeriesMenu({
  title,
  settings,
  defaults,
  widthMetricOptions,
  byMetricDisabledReason,
  onChange,
}: Props) {
  const isBar = settings.chartType === SeriesChartType.bar;
  const isByMetric = settings.lineWidth === BY_METRIC_LINE_WIDTH;
  const labelsOff = settings.valueLabels === SeriesValueLabels.none;
  // Empty option list means no metrics exist — sizing by one would be meaningless.
  const byMetricDisabled =
    isBar || !!byMetricDisabledReason || widthMetricOptions.length === 0;

  const handleLineWidthChange = (value: AxisSeriesSettings['lineWidth']) => {
    if (value !== BY_METRIC_LINE_WIDTH) {
      onChange({ lineWidth: value });

      return;
    }

    onChange({
      lineWidth: value,
      lineWidthMetric:
        settings.lineWidthMetric || widthMetricOptions[0]?.value || '',
    });
  };

  return (
    <Box
      sx={{
        width: 420,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{title}</Typography>
        <Button
          size="small"
          disabled={isPristine(settings, defaults)}
          onClick={() => onChange({ ...defaults })}
        >
          Reset
        </Button>
      </Box>

      <Section title="Chart type">
        <ToggleButtonGroup
          fullWidth
          value={settings.chartType}
          options={Object.values(SeriesChartType).map((type) => {
            return {
              value: type,
              label: (
                <OptionLabel
                  preview={getChartTypePreview(type)}
                  caption={CHART_TYPE_CAPTION[type]}
                />
              ),
            };
          })}
          onChange={(chartType: SeriesChartType) => onChange({ chartType })}
        />
      </Section>

      <Section title="Line width">
        <ToggleButtonGroup
          fullWidth
          value={settings.lineWidth}
          options={[
            ...FIXED_LINE_WIDTHS.map((width) => {
              return {
                value: width,
                disabled: isBar,
                tooltip: isBar ? BAR_DISABLED_REASON : undefined,
                label: (
                  <OptionLabel
                    preview={getLineWidthPreview(width)}
                    caption={`${width}px`}
                  />
                ),
              };
            }),
            {
              value: BY_METRIC_LINE_WIDTH,
              disabled: byMetricDisabled,
              tooltip: isBar
                ? BAR_DISABLED_REASON
                : byMetricDisabledReason || undefined,
              label: (
                <OptionLabel preview={byMetricPreview} caption="By metric" />
              ),
            },
          ]}
          onChange={handleLineWidthChange}
        />
      </Section>

      {isByMetric && (
        <Box sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
          <ColumnSelect
            label={`Metric (${MIN_METRIC_LINE_WIDTH} → ${MAX_METRIC_LINE_WIDTH}px)`}
            options={widthMetricOptions}
            value={settings.lineWidthMetric}
            onChange={(lineWidthMetric) => onChange({ lineWidthMetric })}
          />
        </Box>
      )}

      <Section title="Line style">
        <ToggleButtonGroup
          fullWidth
          value={settings.lineStyle}
          options={Object.values(SeriesLineStyle).map((style) => {
            return {
              value: style,
              disabled: isBar,
              tooltip: isBar ? BAR_DISABLED_REASON : undefined,
              label: (
                <OptionLabel
                  preview={getLineStylePreview(style)}
                  caption={LINE_STYLE_CAPTION[style]}
                />
              ),
            };
          })}
          onChange={(lineStyle: SeriesLineStyle) => onChange({ lineStyle })}
        />
      </Section>

      <Section title="Value labels">
        <ToggleButtonGroup
          fullWidth
          value={settings.valueLabels}
          options={Object.values(SeriesValueLabels).map((labels) => {
            return {
              value: labels,
              label: (
                <OptionLabel
                  preview={valueLabelsPreview[labels]}
                  caption={VALUE_LABELS_CAPTION[labels]}
                />
              ),
            };
          })}
          onChange={(valueLabels: SeriesValueLabels) =>
            onChange({ valueLabels })
          }
        />
      </Section>

      <Section title="Value label text">
        <Tooltip title={labelsOff ? LABELS_OFF_DISABLED_REASON : ''}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Select
              size="small"
              disabled={labelsOff}
              value={settings.valueLabelFontSize}
              onChange={(event) =>
                onChange({
                  valueLabelFontSize: Number(
                    event.target.value,
                  ) as AxisSeriesSettings['valueLabelFontSize'],
                })
              }
              sx={{ width: 96 }}
            >
              {VALUE_LABEL_FONT_SIZES.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}px
                </MenuItem>
              ))}
            </Select>
            <BoldToggle
              size="small"
              value="bold"
              disabled={labelsOff}
              selected={settings.valueLabelBold}
              onChange={() =>
                onChange({ valueLabelBold: !settings.valueLabelBold })
              }
            >
              Bold
            </BoldToggle>
          </Box>
        </Tooltip>
      </Section>

      <Section title="Percent difference label">
        <ToggleButtonGroup
          fullWidth
          value={settings.percentDiff}
          options={Object.values(SeriesPercentDiff).map((mode) => {
            return {
              value: mode,
              label: (
                <OptionLabel
                  preview={percentDiffPreview[mode]}
                  caption={PERCENT_DIFF_CAPTION[mode]}
                />
              ),
            };
          })}
          onChange={(percentDiff: SeriesPercentDiff) =>
            onChange({ percentDiff })
          }
        />
        <Typography variant="caption" sx={{ color: '#7E828F' }}>
          A second line under each value label, and its own column in the
          tooltip. “vs. previous” compares against the nearest earlier point
          that has data, even if that point carries no label.
        </Typography>
      </Section>

      <Section title="Label overlap">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Tooltip title={labelsOff ? LABELS_OFF_DISABLED_REASON : ''}>
            <div>
              <LabeledSwitcher
                disabled={labelsOff}
                label="Hide overlapping labels"
                value={labelsOff ? false : settings.hideOverlappingLabels}
                onChange={(hideOverlappingLabels) =>
                  onChange({ hideOverlappingLabels })
                }
              />
            </div>
          </Tooltip>
          <Typography variant="caption" sx={{ color: '#7E828F', pl: 5.5 }}>
            Within this axis, the higher line keeps its label; its dot stays
            (lines and areas only).
          </Typography>
        </Box>
      </Section>
    </Box>
  );
}

const CHART_TYPE_CAPTION: Record<SeriesChartType, string> = {
  [SeriesChartType.line]: 'Line',
  [SeriesChartType.step]: 'Step',
  [SeriesChartType.area]: 'Area',
  [SeriesChartType.bar]: 'Bar',
};

const LINE_STYLE_CAPTION: Record<SeriesLineStyle, string> = {
  [SeriesLineStyle.solid]: 'Solid',
  [SeriesLineStyle.dashed]: 'Dashed',
  [SeriesLineStyle.dotted]: 'Dotted',
};

const VALUE_LABELS_CAPTION: Record<SeriesValueLabels, string> = {
  [SeriesValueLabels.none]: 'None',
  [SeriesValueLabels.all]: 'All',
  [SeriesValueLabels.startEnd]: 'Start / End',
};

const PERCENT_DIFF_CAPTION: Record<SeriesPercentDiff, string> = {
  [SeriesPercentDiff.none]: 'None',
  [SeriesPercentDiff.fromFirst]: 'vs. first',
  [SeriesPercentDiff.fromPrevious]: 'vs. previous',
};
